import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';
import * as api from '../utils/api';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.7);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Modal = styled.div`
  background: ${props => props.$darkMode ? '#161b22' : '#fff'};
  width: 500px;
  border-radius: 12px;
  padding: 30px;
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-weight: 600;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 6px;
  background: ${props => props.$darkMode ? '#0d1117' : '#f6f8fa'};
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 6px;
  background: ${props => props.$darkMode ? '#0d1117' : '#f6f8fa'};
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  resize: vertical;
  min-height: 80px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  background: ${props => props.$primary ? '#0969da' : (props.$darkMode ? '#21262d' : '#f6f8fa')};
  color: ${props => props.$primary ? '#fff' : (props.$darkMode ? '#e6edf3' : '#24292f')};
  
  &:hover {
    background: ${props => props.$primary ? '#0860ca' : (props.$darkMode ? '#30363d' : '#eaeef2')};
  }
`;

function SaveSpecModal({ spec, darkMode, onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  const handleSave = async () => {
    try {
      const parsed = yaml.load(spec);
      const endpoints = Object.keys(parsed.paths || {}).length;
      
      const specData = {
        id: Date.now().toString(),
        title: title || parsed.info?.title || 'Untitled Spec',
        description: description || parsed.info?.description || '',
        category: category || 'Uncategorized',
        spec: spec,
        endpoints: endpoints,
        savedAt: new Date().toISOString()
      };

      const token = localStorage.getItem('auth-token');
      let savedSpec = specData;
      if (token && token !== 'guest-token') {
        savedSpec = await api.createSpec(specData);
      } else {
        const localSpecs = JSON.parse(localStorage.getItem('local-specs') || '[]');
        localSpecs.unshift(specData);
        localStorage.setItem('local-specs', JSON.stringify(localSpecs));
      }
      
      onSave(savedSpec);
      onClose();
    } catch (e) {
      alert('Error saving spec: ' + e.message);
    }
  };

  return (
    <Overlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <Modal $darkMode={darkMode}>
        <Title $darkMode={darkMode}>Save Spec</Title>
        
        <Label $darkMode={darkMode}>Title</Label>
        <Input 
          $darkMode={darkMode}
          placeholder="My API Spec"
          value={title}
          onChange={e => setTitle(e.target.value)}
        />

        <Label $darkMode={darkMode}>Description</Label>
        <Textarea 
          $darkMode={darkMode}
          placeholder="What does this API do?"
          value={description}
          onChange={e => setDescription(e.target.value)}
        />

        <Label $darkMode={darkMode}>Category</Label>
        <Input 
          $darkMode={darkMode}
          placeholder="e.g. Payment, User Management, etc."
          value={category}
          onChange={e => setCategory(e.target.value)}
        />

        <Buttons>
          <Button $darkMode={darkMode} onClick={onClose}>Cancel</Button>
          <Button $darkMode={darkMode} $primary onClick={handleSave}>Save</Button>
        </Buttons>
      </Modal>
    </Overlay>
  );
}

export default SaveSpecModal;
